import React, { useState, useEffect } from "react";
import "./History.css";
import Select from "react-select";
import PaginationRightIcon from "img/PaginationRight.svg";
import PaginationLeftIcon from "img/PaginationLeft.svg";
import cx from "classnames";
import { useClosedPositions } from "components/Exchange/hooks/useClosedPositions";
import { useTradeHistory } from "components/Exchange/hooks/useTradeHistory";

import DownloadCSVIcon from "img/DownloadCSV.svg";
import ClosedPositionsList from "components/Exchange/ClosedPositionsList";
import TradeHistory from "components/Exchange/TradeHistory";

const History = ({ account, infoTokens, connectWallet, whitelistedTokens }) => {
  const [selectedToken, setSelectedToken] = useState("All");
  const [selectedIsLong, setSelectedIsLong] = useState("All");
  const [activeTab, setActiveTab] = useState("Closed Positions");
  const [currentPage, setCurrentPage] = useState(1);
  const closedPositionsPerPage = 10;
  const tradeHistoryPerPage = 10;

  const ClosedPositions = useClosedPositions(account);
  const tradeHistory = useTradeHistory(account);

  const [filteredClosedPositions, setFilteredClosedPositions] = useState([]);
  const [filteredTradeHistory, setFilteredTradeHistory] = useState([]);

  const isActive = (tab) => {
    return activeTab === tab;
  };

  const tabsOptions = ["Closed Positions", "Trade Activity"];

  useEffect(() => {
    setSelectedToken("All");
    setSelectedIsLong("All");
  }, [activeTab]);

  useEffect(() => {
    const filteredClosedPositions = ClosedPositions.filter((position) => {
      if (selectedToken !== "All" && position.token !== selectedToken) {
        return false;
      }
      if (selectedIsLong !== "All" && position.isLong !== (selectedIsLong === "true")) {
        return false;
      }
      return true;
    });

    const filteredTradeHistory = tradeHistory.filter((trade) => {
      const tradeToken = trade.token.toLowerCase();
      const selectedTokenLower = selectedToken.toLowerCase();

      if (selectedTokenLower !== "all" && tradeToken !== selectedTokenLower) {
        return false;
      }

      if (selectedIsLong !== "All" && trade.isLong !== (selectedIsLong === "true")) {
        return false;
      }

      return true;
    });
    setFilteredClosedPositions(filteredClosedPositions);
    setFilteredTradeHistory(filteredTradeHistory);
    setCurrentPage(1);
  }, [selectedToken, selectedIsLong, ClosedPositions, tradeHistory]);

  const totalClosedPositions = filteredClosedPositions.length;
  const totalClosedPositionPages = Math.ceil(totalClosedPositions / closedPositionsPerPage);
  const totalTradeHistory = filteredTradeHistory.length;
  const totalTradeHistoryPages = Math.ceil(totalTradeHistory / tradeHistoryPerPage);

  const handleTokenChange = (selectedOption) => {
    setSelectedToken(selectedOption?.value || "All");
  };

  const handleIsLongChange = (selectedOption) => {
    setSelectedIsLong(selectedOption?.value || "All");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastClosedPosition = currentPage * closedPositionsPerPage;
  const indexOfFirstClosedPosition = indexOfLastClosedPosition - closedPositionsPerPage;
  const closedPositionsForCurrentPage = filteredClosedPositions.slice(
    indexOfFirstClosedPosition,
    indexOfLastClosedPosition
  );

  const indexOfLastTradeHistory = currentPage * tradeHistoryPerPage;
  const indexOfFirstTradeHistory = indexOfLastTradeHistory - tradeHistoryPerPage;
  const tradeHistoryForCurrentPage = filteredTradeHistory.slice(indexOfFirstTradeHistory, indexOfLastTradeHistory);

  const indexTokens = whitelistedTokens.filter((token) => !token.isStable && !token.isWrapped);

  const options = [
    { value: "All", label: "All" },
    ...indexTokens.map((token, index) => ({
      value: token.address,
      label: token.name,
    })),
  ];

  const isLongOptions = [
    { value: "All", label: "All" },
    { value: "true", label: "Long" },
    { value: "false", label: "Short" },
  ];

  const pageOptionsClosedPosition = Array.from({ length: totalClosedPositionPages }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  const pageOptionsTradeHistory = Array.from({ length: totalTradeHistoryPages }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  const handlePageSelect = (selectedOption) => {
    setCurrentPage(selectedOption?.value || 1);
  };

  return (
    <div className="Overview default-container">
      <div className="Tabs-Wrapper">
        <div className="History-container">
          {tabsOptions.map((opt) => (
            <div className={cx("Tab-option", { active: isActive(opt) })} onClick={() => setActiveTab(opt)} key={opt}>
              {opt}
            </div>
          ))}
        </div>
      </div>

      {activeTab === tabsOptions[0] && (
        <div className="OrderHeaderWrapper">
          <div className="HeaderWrapper">
            <div className="MainHeaderWrapper">
              <span className="MainHeader">Closed Positions</span>
            </div>

            <span className="Subheader muted">View your open Closed Positions.</span>
          </div>
        </div>
      )}

      {activeTab === tabsOptions[1] && (
        <div className="OrderHeaderWrapper">
          <div className="HeaderWrapper">
            <div className="MainHeaderWrapper">
              <span className="MainHeader">Trade Activity</span>
            </div>

            <span className="Subheader muted">View your Trade Activity.</span>
          </div>
        </div>
      )}

      <div className="HistoryFilterWrapper">
        <div className="FilterWrapper">
          <Select
            value={options.find((option) => option.value === selectedToken)}
            onChange={handleTokenChange}
            options={options}
            components={{ IndicatorSeparator: () => null }}
            styles={{
              control: (provided) => ({
                ...provided,
                width: "16.3rem",
                height: "3.5rem",
                borderRadius: "var(--border-radius-sm)",
                backgroundColor: "var(--pallete-background-3)",
                border: "1px solid var(--pallete-border)",
                color: "var(--pallete-border)",
                boxShadow: "none",
              }),

              menu: (provided) => ({
                ...provided,
                backgroundColor: "var(--pallete-background-3)",
                color: "white",
                border: "1px solid var(--pallete-border)",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? "var(--pallete-background-2)" : "#202327",

                color: "#white",
                cursor: "pointer",
                "&:active": {
                  backgroundColor: "var(--pallete-background-3)",
                  outline: "none",
                },
              }),
              singleValue: (provided) => ({
                ...provided,
                color: "var(--pallete-inactive)",
              }),
            }}
          />
          <Select
            value={isLongOptions.find((option) => option.value === selectedIsLong)}
            onChange={handleIsLongChange}
            options={isLongOptions}
            components={{ IndicatorSeparator: () => null }}
            styles={{
              control: (provided) => ({
                ...provided,
                width: "16.3rem",
                height: "3.5rem",
                borderRadius: "var(--border-radius-sm)",
                backgroundColor: "var(--pallete-background-3)",
                border: "1px solid var(--pallete-border)",
                color: "var(--pallete-border)",
                boxShadow: "none",
              }),

              menu: (provided) => ({
                ...provided,
                backgroundColor: "var(--pallete-background-3)",
                color: "white",
                border: "1px solid var(--pallete-border)",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused ? "var(--pallete-background-2)" : "#202327",

                color: "#white",
                cursor: "pointer",
                "&:active": {
                  backgroundColor: "var(--pallete-background-3)",
                  outline: "none",
                },
              }),
              singleValue: (provided) => ({
                ...provided,
                color: "var(--pallete-inactive)",
              }),
            }}
          />
        </div>

        <button className="DownloadButton transition-effect">
          <img src={DownloadCSVIcon} alt="downloadCSV" className="downloadCSVIcon" />
          <span> Download CSV</span>
        </button>
      </div>

      {activeTab === tabsOptions[0] && (
        <>
          <ClosedPositionsList
            account={account}
            infoTokens={infoTokens}
            connectWallet={connectWallet}
            positions={closedPositionsForCurrentPage}
            className="ClosedPositionList"
          />
          <div className="Pagination">
            <span className="PaginationPageNumber muted">Total: {totalClosedPositionPages} pages</span>

            <div className="PaginationButtonWrapper">
              <div className="PaginationDropdown">
                <span className="muted">The page you are on</span>
                <Select
                  value={pageOptionsClosedPosition.find((option) => option.value === currentPage)}
                  onChange={handlePageSelect}
                  options={pageOptionsClosedPosition}
                  components={{ IndicatorSeparator: () => null }}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      width: "7rem",
                      height: "2.5rem",
                      backgroundColor: "var(--pallete-background-1)",
                      border: "1px solid var(--pallete-border)",
                      borderRadius: "var(--border-radius-sm)",
                      color: "var(--pallete-border)",
                      boxShadow: "none",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? "var(--pallete-border)" : "var(--pallete-background-1)",
                      cursor: "pointer",
                      color: "white",

                      "&:active": {
                        backgroundColor: "var(--pallete-background-1)",
                        outline: "none",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: "var(--pallete-background-1)",
                      color: "white",
                      border: "1px solid var(--pallete-border)",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "var(--pallete-inactive)",
                    }),
                  }}
                />
              </div>

              <div className="PaginationButtons">
                <button
                  className="PaginationButton"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <img src={PaginationLeftIcon} alt="pagination" />
                </button>
                <span className="PageNumber">{currentPage}</span>
                <button
                  className="PaginationButton"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalClosedPositionPages}
                >
                  <img src={PaginationRightIcon} alt="pagination" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === tabsOptions[1] && (
        <>
          <TradeHistory
            account={account}
            infoTokens={infoTokens}
            connectWallet={connectWallet}
            tradeHistory={tradeHistoryForCurrentPage}
            className="TradeHistoryList"
          />
          <div className="Pagination">
            <span className="PaginationPageNumber muted">Total: {totalTradeHistoryPages} pages</span>

            <div className="PaginationButtonWrapper">
              <div className="PaginationDropdown">
                <span className="muted">The page you are on</span>
                <Select
                  value={pageOptionsTradeHistory.find((option) => option.value === currentPage)}
                  onChange={handlePageSelect}
                  options={pageOptionsTradeHistory}
                  components={{ IndicatorSeparator: () => null }}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      width: "7rem",
                      height: "2.5rem",
                      backgroundColor: "var(--pallete-background-1)",
                      border: "1px solid var(--pallete-border)",
                      borderRadius: "var(--border-radius-sm)",
                      color: "var(--pallete-border)",
                      boxShadow: "none",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? "var(--pallete-border)" : "var(--pallete-background-1)",
                      cursor: "pointer",
                      color: "white",
                      "&:active": {
                        backgroundColor: "var(--pallete-background-1)",
                        outline: "none",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: "var(--pallete-background-1)",
                      color: "white",
                      border: "1px solid var(--pallete-border)",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "var(--pallete-inactive)",
                    }),
                  }}
                />
              </div>

              <div className="PaginationButtons">
                <button
                  className="PaginationButton"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <img src={PaginationLeftIcon} alt="pagination" />
                </button>
                <span className="PageNumber">{currentPage}</span>
                <button
                  className="PaginationButton"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalTradeHistoryPages}
                >
                  <img src={PaginationRightIcon} alt="pagination" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default History;
